import { DataGrid, GridColDef } from "@mui/x-data-grid"
import { translate } from "app/language/service"
import { IRootState } from "app/model"
import { formatDate } from "app/util/date/tools/common"
import { UnexpectedErrorPage } from "page/components/UnexpectedErrorPage"
import React from "react"
import { connect } from "react-redux"
import { IUserReport, REPORTS_MY_LIST_DOM_ID } from "reports/model"
import { loadMyReports } from "reports/tools/common"
import { FlexBox } from "ui/components/FlexBox"
import { LinearLoader } from "ui/components/LinearLoader"
import { getDataGridTexts } from "ui/tools/list"
import { MyReportsListActions } from "./MyReportsListActions"

interface IMyReportsListConnectProps {
  reports: IUserReport[] | null
  isLoading: boolean
  hasError: boolean
}

const MyReportsListFn = React.memo(({ reports, isLoading, hasError }: IMyReportsListConnectProps) => {
  React.useEffect(() => {
    if (reports !== null || isLoading || hasError) {
      return
    }
    loadMyReports()
  }, [reports === null])
  if (hasError) {
    return <UnexpectedErrorPage />
  }
  if (reports === null || isLoading) {
    return <LinearLoader sx={{ m: "2rem" }} />
  }
  const columns: Array<GridColDef<IUserReport>> = [
    {
      field: "name",
      headerName: translate("label.name"),
      flex: 1
    },
    {
      field: "description",
      headerName: translate("label.description"),
      flex: 2
    },
    {
      field: "createdAt",
      headerName: translate("label.creationDate"),
      flex: 1,
      renderCell: ({ row }) => formatDate(row.createdAt)
    },
    {
      field: "updatedAt",
      headerName: translate("label.modificationDate"),
      flex: 1,
      renderCell: ({ row }) => formatDate(row.updatedAt, { displayTime: true })
    },
    {
      field: "actions",
      headerName: translate("label.actions"),
      maxWidth: 100,
      sortable: false,
      flex: 1,
      renderCell: params => <MyReportsListActions report={params.row} />
    }
  ]
  return (
    <FlexBox
      id={REPORTS_MY_LIST_DOM_ID}
      vertical
      gap
      ai="start"
      fullWidth
      sx={{
        "height": "calc(100% - 3rem)",
        "minHeight": "calc(100% - 3rem)",
        "maxHeight": "calc(100% - 3rem)",
        "& .icon": {
          color: theme => theme.palette.text.primary,
          fontSize: "1.2rem"
        }
      }}>
      <DataGrid
        rows={reports}
        columns={columns}
        autoPageSize
        disableSelectionOnClick
        localeText={getDataGridTexts()}
        sx={{ width: "100%", height: "22rem" }}
        pagination
      />
    </FlexBox>
  )
})

export const MyReportsList = connect(
  (state: IRootState): IMyReportsListConnectProps => ({
    reports: state.reports.userReports,
    isLoading: state.reports.isListLoading,
    hasError: state.reports.hasListLoadingError
  })
)(MyReportsListFn)
